import { useEffect } from 'react';
import { MeshPhysicalMaterial } from 'three';
import { useShallow } from 'zustand/shallow';

import { useSheetMaterialLoader } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useSheetMaterialLoader';
import { applyMaterialQuality } from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface MaterialUpdateHandlerProps {
    isMainCanvas: boolean;
}

/**
 * Updates batched mesh materials and applies material quality settings in the main and navigator scene.
 */
export const useMaterialUpdateHandler = ({ isMainCanvas }: MaterialUpdateHandlerProps) => {
    const sheetMaterial = useSheetMaterialLoader();
    const materialQuality = usePlacementStore(useShallow((state) => state.graphicsSettings.materialQuality));
    const meshes = usePlacementStore(
        useShallow((state) => (isMainCanvas ? state.batchedMeshes : state.navigatorBatchedMeshes))
    );

    // apply material quality changes
    useEffect(() => {
        const batchedMeshes = usePlacementStore.getState().getBatchedMeshes(isMainCanvas);
        if (!batchedMeshes.physical) {
            return;
        }

        applyMaterialQuality(batchedMeshes.physical.material as MeshPhysicalMaterial, materialQuality, false);

        if (batchedMeshes.sheet?.material instanceof MeshPhysicalMaterial) {
            const targetSheetMaterial = batchedMeshes.sheet.material as MeshPhysicalMaterial;

            if (sheetMaterial?.map && targetSheetMaterial.map !== sheetMaterial.map) {
                targetSheetMaterial.map = sheetMaterial.map;
                targetSheetMaterial.needsUpdate = true;
            }

            applyMaterialQuality(targetSheetMaterial, materialQuality, true);
        }
    }, [materialQuality, meshes, isMainCanvas, sheetMaterial]);
};
